import { useCallback, useEffect, useRef, useState } from 'react'

import { LayerTilesetPaths } from './useHasKestrel3DTiles'

/** State for a single layer in the layer swap system */
export interface LayerState {
  /** Layer name (e.g., 'rgb', 'day_ir', 'night_ir') */
  layerName: string
  /** Paths to tileset folders for this layer */
  tilesetPaths: string[]
  status: 'active' | 'loading' | 'fading-out'
  opacity: number
}

interface UseLayerManagerOptions {
  availableLayers: string[]
  layerTilesets: Map<string, LayerTilesetPaths>
  setTilesFolder?: (folder: string) => void
  fadeInDuration?: number
}

export function useLayerManager({
  availableLayers,
  layerTilesets,
  setTilesFolder,
  fadeInDuration = 250,
}: UseLayerManagerOptions) {
  const [layers, setLayers] = useState<LayerState[]>([])
  const [activeLayer, setActiveLayer] = useState<string>('rgb')
  const hasInitialized = useRef(false)
  const animationRef = useRef<number | null>(null)

  // Initialize with first available layer (only once)
  useEffect(() => {
    if (hasInitialized.current || availableLayers.length === 0) return
    hasInitialized.current = true
    const initialLayerName = availableLayers.includes('rgb')
      ? 'rgb'
      : availableLayers[0]
    const initialTilesets = layerTilesets.get(initialLayerName)
    const tilesetPaths = initialTilesets?.tilesetPaths ?? [initialLayerName]
    setLayers([
      {
        layerName: initialLayerName,
        tilesetPaths,
        status: 'active',
        opacity: 1,
      },
    ])
    setActiveLayer(initialLayerName)
    setTilesFolder?.(initialLayerName)
  }, [availableLayers, layerTilesets, setTilesFolder])

  // Cleanup animation on unmount
  useEffect(() => {
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
    }
  }, [])

  // Animate fade-in of new layer
  const startFadeIn = useCallback(
    (layerName: string) => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }

      const startTime = performance.now()

      const animate = () => {
        const elapsed = performance.now() - startTime
        const progress = Math.min(elapsed / fadeInDuration, 1)
        const eased = 1 - (1 - progress) * (1 - progress)

        setLayers((prevLayers) =>
          prevLayers.map((layer) => {
            if (layer.layerName === layerName) {
              return { ...layer, opacity: eased }
            }
            return layer
          })
        )

        if (progress < 1) {
          animationRef.current = requestAnimationFrame(animate)
        } else {
          setLayers((prevLayers) =>
            prevLayers.map((l) =>
              l.layerName === layerName
                ? { ...l, status: 'active' as const, opacity: 1 }
                : l
            )
          )
          animationRef.current = null
        }
      }

      animationRef.current = requestAnimationFrame(animate)
    },
    [fadeInDuration]
  )

  // Handle layer change request
  const handleLayerChange = useCallback(
    (newLayerName: string) => {
      if (newLayerName === activeLayer) return
      if (layers.some((l) => l.layerName === newLayerName)) return

      const newTilesets = layerTilesets.get(newLayerName)
      const tilesetPaths = newTilesets?.tilesetPaths ?? [newLayerName]

      setLayers((prevLayers) => [
        ...prevLayers,
        {
          layerName: newLayerName,
          tilesetPaths,
          status: 'loading' as const,
          opacity: 0,
        },
      ])
      setActiveLayer(newLayerName)
      setTilesFolder?.(newLayerName)
    },
    [activeLayer, layers, layerTilesets, setTilesFolder]
  )

  // Handle when a new layer finishes loading
  const handleLayerLoaded = useCallback(
    (layerName: string) => {
      const tilesets = layerTilesets.get(layerName)
      const tilesetPaths = tilesets?.tilesetPaths ?? [layerName]
      setLayers([
        { layerName, tilesetPaths, status: 'active' as const, opacity: 0 },
      ])
      startFadeIn(layerName)
    },
    [layerTilesets, startFadeIn]
  )

  return {
    layers,
    activeLayer,
    loadingLayer: layers.find((l) => l.status === 'loading')?.layerName,
    handleLayerChange,
    handleLayerLoaded,
  }
}
